import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  PrebookCancellationPenalty,
  StructuredPolicies,
} from "@/api/generated/schemas";

export interface DraftGuest {
  title?: "MR" | "MRS" | "MS" | "MISS" | "MASTER";
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  nationality?: string;
  room_index: number;
  is_child: boolean;
  age?: number;
}

export interface DraftRoomConfig {
  adults: number;
  children_ages: number[];
}

export interface PrebookSnap {
  offer_token_id?: string;
  locked_price: number;
  currency: string;
  /**
   * B-H: Stripe-ready amount in ISO 4217 minor units. Populated by the BE
   * (`PrebookResponse.amount_minor`). Optional only for backward compat with
   * snapshots persisted before migration v7 — the migration ne fornisce un
   * fallback `Math.round(locked_price * 100)`.
   */
  amount_minor?: number;
  cancellation_summary?: string;
  remarks?: string;
  cancellation_deadline?: string;
  structured_policies?: StructuredPolicies | null;
  cancellation_penalties?: PrebookCancellationPenalty[];
  price_changed?: boolean;
}

export interface BookingDraftState {
  offerTokenIds: string[];
  ssotHotelId?: string;
  searchId?: string;
  rooms?: DraftRoomConfig[];
  checkIn?: string;
  checkOut?: string;
  expectedPrice?: number;
  guests: DraftGuest[];
  specialRequests?: string;
  agencyReference?: string;
  contactEmail?: string;
  contactPhone?: string;
  agreementsAccepted: boolean;
  priceChangeAccepted: boolean;
  idempotencyKey?: string;
  paymentIntentId?: string;
  paymentStatus?: "idle" | "processing" | "succeeded" | "failed";
  bookingGroupId?: string;
  prebookSnapshots: PrebookSnap[];
  // Backward-compat: alias to prebookSnapshots[0]
  prebookSnapshot?: PrebookSnap;
  /**
   * B-I: Idempotency-Key persistente per (booking_id, action).
   *
   * Difende contro retry user-driven (es. utente refresha la pagina di cancel
   * o ri-clicca "Cancella" dopo un transient 504). Senza, axios-instance
   * genera una nuova UUID ad ogni request e il BE perde l'idempotency.
   *
   * Le chiavi sono per `booking_id` (UUID stringa). Pulizia: `clearBookingKeys`
   * dopo success/404 per evitare accumulo indefinito.
   */
  cancelIdempotencyKeys: Record<string, string>;
  amendIdempotencyKeys: Record<string, string>;
}

const initialState: BookingDraftState = {
  offerTokenIds: [],
  guests: [],
  agreementsAccepted: false,
  priceChangeAccepted: false,
  paymentStatus: "idle",
  prebookSnapshots: [],
  cancelIdempotencyKeys: {},
  amendIdempotencyKeys: {},
};

const bookingDraftSlice = createSlice({
  name: "bookingDraft",
  initialState,
  reducers: {
    startDraft(
      state,
      action: PayloadAction<{
        offerTokenIds: string[];
        ssotHotelId?: string;
        searchId?: string;
        rooms?: DraftRoomConfig[];
        checkIn?: string;
        checkOut?: string;
        expectedPrice?: number;
      }>,
    ) {
      state.offerTokenIds = action.payload.offerTokenIds;
      state.ssotHotelId = action.payload.ssotHotelId;
      state.searchId = action.payload.searchId;
      state.rooms = action.payload.rooms;
      state.checkIn = action.payload.checkIn;
      state.checkOut = action.payload.checkOut;
      state.expectedPrice = action.payload.expectedPrice;
      state.guests = [];
      state.idempotencyKey = crypto.randomUUID();
      state.paymentStatus = "idle";
      state.agreementsAccepted = false;
      state.priceChangeAccepted = false;
      state.prebookSnapshots = [];
      state.prebookSnapshot = undefined;
      state.bookingGroupId = undefined;
    },
    setGuests(state, action: PayloadAction<DraftGuest[]>) {
      state.guests = action.payload;
    },
    setContact(
      state,
      action: PayloadAction<{
        email?: string;
        phone?: string;
        agencyReference?: string;
        specialRequests?: string;
      }>,
    ) {
      state.contactEmail = action.payload.email;
      state.contactPhone = action.payload.phone;
      state.agencyReference = action.payload.agencyReference;
      state.specialRequests = action.payload.specialRequests;
    },
    setPrebookSnapshot(state, action: PayloadAction<PrebookSnap>) {
      state.prebookSnapshot = action.payload;
      state.prebookSnapshots = [action.payload];
    },
    setPrebookSnapshots(state, action: PayloadAction<PrebookSnap[]>) {
      state.prebookSnapshots = action.payload;
      state.prebookSnapshot = action.payload[0];
    },
    setAgreements(state, action: PayloadAction<boolean>) {
      state.agreementsAccepted = action.payload;
    },
    setPriceChangeAccepted(state, action: PayloadAction<boolean>) {
      state.priceChangeAccepted = action.payload;
    },
    setBookingGroupId(state, action: PayloadAction<string | undefined>) {
      state.bookingGroupId = action.payload;
    },
    setPaymentIntent(
      state,
      action: PayloadAction<{
        id: string;
        status: BookingDraftState["paymentStatus"];
      }>,
    ) {
      state.paymentIntentId = action.payload.id;
      state.paymentStatus = action.payload.status;
    },
    setPaymentStatus(
      state,
      action: PayloadAction<BookingDraftState["paymentStatus"]>,
    ) {
      state.paymentStatus = action.payload;
    },
    /**
     * B-I: idempotency-key persistente per cancel/amend di un booking.
     * `ensure*` è idempotente: genera la chiave solo se ancora assente.
     */
    ensureCancelKey(state, action: PayloadAction<{ bookingId: string }>) {
      const { bookingId } = action.payload;
      if (!state.cancelIdempotencyKeys[bookingId]) {
        state.cancelIdempotencyKeys[bookingId] = crypto.randomUUID();
      }
    },
    ensureAmendKey(state, action: PayloadAction<{ bookingId: string }>) {
      const { bookingId } = action.payload;
      if (!state.amendIdempotencyKeys[bookingId]) {
        state.amendIdempotencyKeys[bookingId] = crypto.randomUUID();
      }
    },
    /**
     * Pulisce le chiavi di un singolo booking (chiamato post success/404
     * per evitare accumulo indefinito).
     */
    clearBookingKeys(state, action: PayloadAction<{ bookingId: string }>) {
      const { bookingId } = action.payload;
      delete state.cancelIdempotencyKeys[bookingId];
      delete state.amendIdempotencyKeys[bookingId];
    },
    clearDraft() {
      return initialState;
    },
  },
});

export const {
  startDraft,
  setGuests,
  setContact,
  setPrebookSnapshot,
  setPrebookSnapshots,
  setAgreements,
  setPriceChangeAccepted,
  setBookingGroupId,
  setPaymentIntent,
  setPaymentStatus,
  ensureCancelKey,
  ensureAmendKey,
  clearBookingKeys,
  clearDraft,
} = bookingDraftSlice.actions;
export { bookingDraftSlice };
export default bookingDraftSlice.reducer;
